import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { SYNC_LABEL, fmtDate, getJson, text, yesNo } from './api'

export function useJson<T>(url: string | null) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string | null }>({ data: null, loading: Boolean(url), error: null })
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!url) { setState({ data: null, loading: false, error: null }); return }
    let alive = true
    setState((s) => ({ ...s, loading: true, error: null }))
    getJson<T>(url)
      .then((d) => { if (alive) setState({ data: d, loading: false, error: null }) })
      .catch((e: unknown) => { if (alive) setState({ data: null, loading: false, error: String((e as Error)?.message ?? e) }) })
    return () => { alive = false }
  }, [url, tick])
  return { ...state, reload: () => setTick((t) => t + 1) }
}

export function Badge({ kind, children, title }: { kind: 'ok' | 'muted' | 'warn' | 'bad' | 'info'; children: ReactNode; title?: string }) {
  return <span className={`mbr-badge mbr-badge--${kind}`} title={title}>{children}</span>
}

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null
  const s = status.toLowerCase()
  const kind = s === 'current' ? 'ok' : s === 'deceased' || s === 'deleted in ontrack' || s === 'cancelled' ? 'bad' : s === 'gone away' || s === 'lapsed' || s === 'archived' ? 'warn' : 'muted'
  return <Badge kind={kind}>{status}</Badge>
}

export function SyncBadge({ status, discrepancies }: { status: string | null | undefined; discrepancies?: number | null }) {
  if (!status) return null
  const kind = status === 'in_sync' ? 'ok' : status === 'discrepancies' ? 'warn' : status === 'deleted_in_ontrack' || status === 'write_failed' ? 'bad' : status === 'local_only' ? 'info' : 'muted'
  const label = status === 'discrepancies' && discrepancies ? `${discrepancies} discrepanc${discrepancies === 1 ? 'y' : 'ies'}` : SYNC_LABEL[status] ?? status
  return <Badge kind={kind} title="How this record compares with Ontrack (and, from the next build, Donorfy and GoCardless)">{label}</Badge>
}

export function MasterBadge({ master }: { master: string | null | undefined }) {
  if (!master) return <Badge kind="muted" title="No sync master is set: nothing is overwritten by a pull; differences are listed as discrepancies.">No master</Badge>
  return <Badge kind="info">Master: {master === 'membership' ? 'Membership' : master === 'ontrack' ? 'Ontrack' : 'Donorfy'}</Badge>
}

/** A definition list of fields; empty values shown dimmed so a blank is visibly a blank. */
export function Dl({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="mbr-dl">
      {items.map(([k, v], i) => {
        const empty = v === null || v === undefined || v === '' || v === false
        return (
          <FragmentRow key={`${k}-${i}`} k={k} v={empty ? '—' : v} empty={empty} />
        )
      })}
    </dl>
  )
}
function FragmentRow({ k, v, empty }: { k: string; v: ReactNode; empty: boolean }) {
  return (<><dt>{k}</dt><dd className={empty ? 'mbr-empty-val' : undefined}>{v}</dd></>)
}

export const val = (r: Record<string, unknown>, k: string) => text(r[k])
export const dateVal = (r: Record<string, unknown>, k: string) => fmtDate(r[k] as string | null)
export const boolVal = (r: Record<string, unknown>, k: string) => yesNo(r[k] as boolean | null)

export function MemberLink({ id, children }: { id: number | null | undefined; children: ReactNode }) {
  if (!id) return <>{children}</>
  return <Link className="mbr-link" to={`/membership/members/${id}`}>{children}</Link>
}
export function MembershipLink({ id, children }: { id: number | null | undefined; children: ReactNode }) {
  if (!id) return <>{children}</>
  return <Link className="mbr-link" to={`/membership/memberships/${id}`}>{children}</Link>
}

export function Kpi({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mbr-kpi">
      <div className="mbr-kpi-label">{label}</div>
      <div className="mbr-kpi-value">{value}</div>
      {sub && <div className="mbr-kpi-sub">{sub}</div>}
    </div>
  )
}

export function Placeholder({ title, children }: { title: string; children: ReactNode }) {
  return <div className="mbr-placeholder"><strong>{title}</strong> — {children}</div>
}
